import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Constraint,
  ConstraintTabBarType,
  ConcernCategory,
} from "../../types/constraint";
import { Client, ConstraintEmployee } from "../../types/ganttChart";

type ConstraintSlice = {
  selectedConstraintTab: ConstraintTabBarType;
  employees: ConstraintEmployee[];
  clients: Client[];
  categories: ConcernCategory[];
};

const initialState: ConstraintSlice = {
  selectedConstraintTab: "Employee",
  employees: [],
  clients: [],
  categories: [],
};

const constraintSlice = createSlice({
  name: "constraint",
  initialState,
  reducers: {
    setSelectedConstraintTab(
      state,
      action: PayloadAction<ConstraintTabBarType>,
    ) {
      state.selectedConstraintTab = action.payload;
    },
    setEmployees(state, action: PayloadAction<ConstraintEmployee[]>) {
      state.employees = action.payload;
    },
    setClients(state, action: PayloadAction<Client[]>) {
      state.clients = action.payload;
    },
    setCategories(state, action: PayloadAction<ConcernCategory[]>) {
      state.categories = action.payload;
    },
    addCategory(state, action: PayloadAction<ConcernCategory>) {
      state.categories.push(action.payload);
    },
    updateCategory(state, action: PayloadAction<ConcernCategory>) {
      const index = state.categories.findIndex(
        (c) => c.id === action.payload.id,
      );
      if (index !== -1) state.categories[index] = action.payload;
    },
    removeCategory(state, action: PayloadAction<string>) {
      state.categories = state.categories.filter(
        (c) => c.id !== action.payload,
      );
    },
    addConstraintToEmployee(
      state,
      action: PayloadAction<{ employeeRef: string; constraint: Constraint }>,
    ) {
      const employee = state.employees.find(
        (e) => e.employeeRef === action.payload.employeeRef,
      );
      if (employee) employee.constraints.push(action.payload.constraint);
    },
    addConstraintToClient(
      state,
      action: PayloadAction<{ clientId: number; constraint: Constraint }>,
    ) {
      const client = state.clients.find(
        (c) => c.idServiceClient === action.payload.clientId,
      );
      if (client) {
        if (!client.constraints) client.constraints = [];
        client.constraints.push(action.payload.constraint);
      }
    },
    removeConstraintFromClient(
      state,
      action: PayloadAction<{ clientId: number; constraintId: number }>,
    ) {
      const client = state.clients.find(
        (c) => c.idServiceClient === action.payload.clientId,
      );
      if (client?.constraints) {
        client.constraints = client.constraints.filter(
          (c) => c.id !== action.payload.constraintId,
        );
      }
    },
    updateConstraintOnEmployee(
      state,
      action: PayloadAction<{ employeeRef: string; constraint: Constraint }>,
    ) {
      const employee = state.employees.find(
        (e) => e.employeeRef === action.payload.employeeRef,
      );
      if (!employee) return;
      const index = employee.constraints.findIndex(
        (c) => c.id === action.payload.constraint.id,
      );
      if (index !== -1) employee.constraints[index] = action.payload.constraint;
    },
    removeConstraintFromEmployee(
      state,
      action: PayloadAction<{ employeeRef: string; constraintId: number }>,
    ) {
      const employee = state.employees.find(
        (e) => e.employeeRef === action.payload.employeeRef,
      );
      if (employee) {
        employee.constraints = employee.constraints.filter(
          (c) => c.id !== action.payload.constraintId,
        );
      }
    },
  },
});

export const {
  setSelectedConstraintTab,
  setEmployees,
  setClients,
  setCategories,
  addCategory,
  updateCategory,
  removeCategory,
  addConstraintToEmployee,
  updateConstraintOnEmployee,
  addConstraintToClient,
  removeConstraintFromClient,
  removeConstraintFromEmployee,
} = constraintSlice.actions;
export default constraintSlice.reducer;
